// Talk's fillers — T120 (26 Sep 2026), by stage since T120a. While `ask_vera` runs, the brain says
// what a person says while they look something up, in her voice, over ElevenLabs' typing: a line
// that fits where the lookup is ("Okay, pulling it up...", "Going through them now...", "One more
// thing to check...", "Okay, nearly there..."), a quieter "Hmm..." or a hum now and then when
// there is nothing new to say, and "Okay, got it" as the answer arrives, when a line was heard.
//
// THE STAGE IS THE LOOKUP'S OWN, never guessed from the clock (Natanel, T120a: "saying 'nearly
// there' after the first two seconds is silly, the fillers should be progressive and indicative of
// the stage in the lookup they are in"). The find's stream says when the model reaches for a tool
// (`looking`) and when its answer starts being written (`writing`), and `Filling` takes both
// (voice-tools.ts passes them on). So "nearly there" is said only while the answer is being written,
// "one more thing" only when it went back for more; the clock decides only when she may speak, and
// that she says she is taking long when she is. A stage she has spoken of is not spoken of again
// until the lookup moves on, which is also what keeps her from chattering.
//
// ElevenLabs cannot speak during a client tool itself (a probe on 26 Sep 2026: its soft-timeout
// fillers stay quiet while one runs), so the clips are the brain's to play. The lines and the
// timings are `@vera/agent/voice-fillers`; the clips are rendered in her voice by
// `pnpm --filter @vera/bff voice:fillers` into ./voice-fillers/ and fingerprinted by the build.
//
// THEY STOP WHEN ANYONE ELSE SPEAKS (T436, 23 Oct 2026, Natanel: "two brain voices speaking at the
// same time"): the recruiter talking on mid-lookup cancels the lookup's fillers (elevenlabs.ts), the
// clip playing halted and no closing line, for every lookup running, not only the last.
//
// HER EARS ARE CLOSED WHILE A CLIP PLAYS. The clip comes out of the same speakers the microphone
// hears, and ElevenLabs would take "Nearly there" for the recruiter talking over her: `mute` shuts
// the microphone for the length of a clip and gives it back as the recruiter left it.
import {
  FILLER_EVERY_MS,
  FILLER_FIRST_MS,
  FILLER_JITTER_MS,
  FILLER_LONG_EVERY_MS,
  FILLER_LONG_MS,
  MURMUR_CHANCE,
  MURMUR_VOLUME,
  VOICE_FILLERS,
  type FillerStage,
  type VoiceFiller,
} from '@vera/agent/voice-fillers';
import type { LookupStages } from './voice-tools.ts';

/** The rendered clips by id, as the build fingerprints them (./voice-fillers/<id>.mp3). */
export const FILLER_CLIPS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>('./voice-fillers/*.mp3', {
      eager: true,
      query: '?url',
      import: 'default',
    }),
  ).map(([path, url]) => [path.replace(/^.*\/([^/]+)\.mp3$/, '$1'), url]),
);

/** Something that plays a clip to its end. */
export interface FillerAudio {
  /** Plays the clip, at `volume` (0 to 1, full when not given); resolves when it has ended, failed to play, or been halted. */
  play(id: string, volume?: number): Promise<void>;
  /** Stops a clip at once. */
  halt(): void;
}

export interface FillerOptions {
  /** The microphone: shut for a clip, `false` hands it back as the recruiter had it. */
  mute(on: boolean): void;
  random?: () => number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (timer: unknown) => void;
}

/** One lookup's fillers: told the lookup's stages as they happen (`LookupStages`), then its end. */
export interface Filling extends LookupStages {
  /**
   * The answer is in. Nothing starts after this; a clip playing is let finish, as a person would
   * rather than stopping mid-word, and the closing line follows when a line (not only a murmur) was
   * heard. True then.
   */
  finish(): Promise<boolean>;
  /** The conversation is over, or the recruiter spoke again: silence now, no closing line. */
  cancel(): void;
}

/**
 * One lookup's fillers, one clip at a time. After FILLER_FIRST_MS, and then FILLER_EVERY_MS (give
 * or take FILLER_JITTER_MS) after each clip ends, she may speak: a line if the lookup has moved on
 * since her last one (`nextStage`), otherwise, now and then, a murmur. ElevenLabs' typing goes on
 * under all of it.
 */
export function startFillers(audio: FillerAudio, options: FillerOptions): Filling {
  const random = options.random ?? Math.random;
  const now = options.now ?? (() => Date.now());
  const setTimer = options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer =
    options.clearTimer ??
    ((timer: unknown) => clearTimeout(timer as ReturnType<typeof setTimeout>));
  const pools = new Map<VoiceFiller['when'], VoiceFiller[]>();
  for (const filler of VOICE_FILLERS)
    pools.set(filler.when, [...(pools.get(filler.when) ?? []), filler]);
  // Never the same line of a kind twice running.
  const last = new Map<VoiceFiller['when'], string>();
  const pick = (kind: VoiceFiller['when']) => {
    const pool = pools.get(kind) ?? [];
    const fresh = pool.filter((f) => f.id !== last.get(kind));
    const choices = fresh.length > 0 ? fresh : pool;
    const chosen = choices[Math.floor(random() * choices.length)]!;
    last.set(kind, chosen.id);
    return chosen.id;
  };
  const jitter = (spread: number) => (random() * 2 - 1) * spread;

  // Where the lookup is, as its stream told it (`looking`, `writing`). Cast, not annotated: the loop
  // below runs in an immediately invoked function, which TypeScript would read as only ever seeing
  // the first value, though `looking` and `writing` change it between its turns.
  const began = now();
  let stage = 'thinking' as 'thinking' | 'looking' | 'writing';
  let rounds = 0;
  // What she has said: lines (murmurs are not counted), the last line's stage, the lookup's rounds
  // at that line, and when she last said she was taking long.
  let heard = 0;
  let lastStage: FillerStage | null = null;
  let roundsAtLast = 0;
  let lastLongAt: number | null = null;
  let lastWasMurmur = false;

  /** The stage worth a line now, or null: nothing has moved on since her last one. */
  const nextStage = (): FillerStage | null => {
    const elapsed = now() - began;
    // The answer is being written: "nearly there", once, and only after she has said something,
    // since a first line now would be followed at once by the answer.
    if (stage === 'writing') return heard > 0 && lastStage !== 'write' ? 'write' : null;
    if (heard === 0) return rounds > 0 ? 'look' : 'start';
    if (rounds > roundsAtLast) return lastStage === 'start' ? 'look' : 'again';
    if (
      elapsed >= FILLER_LONG_MS &&
      (lastLongAt === null || elapsed - lastLongAt >= FILLER_LONG_EVERY_MS)
    ) {
      return 'long';
    }
    return null;
  };

  let over = false;
  let wake: (() => void) | null = null;
  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      const timer = setTimer(() => {
        wake = null;
        resolve();
      }, ms);
      wake = () => {
        clearTimer(timer);
        wake = null;
        resolve();
      };
    });
  const say = async (id: string, volume = 1) => {
    options.mute(true);
    try {
      await audio.play(id, volume);
    } finally {
      options.mute(false);
    }
  };

  const loop = (async () => {
    await sleep(FILLER_FIRST_MS);
    while (!over) {
      const next = nextStage();
      if (next) {
        heard += 1;
        lastStage = next;
        roundsAtLast = rounds;
        lastWasMurmur = false;
        if (next === 'long') lastLongAt = now() - began;
        await say(pick(next));
      } else if (stage !== 'writing' && !lastWasMurmur && random() < MURMUR_CHANCE) {
        lastWasMurmur = true;
        await say(pick('murmur'), MURMUR_VOLUME);
      } else {
        lastWasMurmur = false;
      }
      if (over) break;
      await sleep(FILLER_EVERY_MS + jitter(FILLER_JITTER_MS));
    }
  })();

  const quiet = () => {
    over = true;
    wake?.();
  };

  return {
    looking() {
      rounds += 1;
      if (stage !== 'writing') stage = 'looking';
    },
    writing() {
      stage = 'writing';
    },
    async finish() {
      if (over) return false;
      quiet();
      await loop;
      if (heard === 0) return false;
      await say(pick('done'));
      return true;
    },
    cancel() {
      quiet();
      audio.halt();
    },
  };
}

/** The browser's player: one `Audio` element a clip, the bundle's fingerprinted URL. */
export function browserFillerAudio(scope: object): FillerAudio | null {
  const { Audio: AudioElement } = scope as { Audio?: typeof Audio };
  if (!AudioElement) return null;
  // T436a: every clip playing, since two lookups' clips can overlap and a halt stops them all.
  const playing = new Set<{ element: HTMLAudioElement; done: () => void }>();
  return {
    play(id, volume = 1) {
      const url = FILLER_CLIPS[id];
      if (!url) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const element = new AudioElement(url);
        element.volume = volume;
        const clip = { element, done: () => {} };
        const done = () => {
          playing.delete(clip);
          resolve();
        };
        clip.done = done;
        playing.add(clip);
        element.addEventListener('ended', done, { once: true });
        element.addEventListener('error', done, { once: true });
        // Talk opens from a tap, so the page may play sound; a refusal is a silent clip, not an error.
        element.play().catch(done);
      });
    },
    halt() {
      for (const clip of [...playing]) {
        clip.element.pause();
        clip.done();
      }
    },
  };
}
